<script setup lang="ts">
    /* eslint-disable vue/no-v-html */
    import { type Message } from '@chatbot/typings/types';
    import { getContrastTextColor } from '@chatbot/utils/color';
    import DOMPurify from 'dompurify';
    import { marked } from 'marked';

    // 2. Props
    const props = defineProps<{
        message: Message;
        primaryColor?: string;
    }>();

    // Configure marked options for better security and formatting
    marked.setOptions({
        breaks: true, // Convert \n to <br>
        gfm: true, // GitHub Flavored Markdown
    });

    // 3. Computed properties
    const renderedMarkdown: ComputedRef<string> = computed(() => {
        try {
            // Parse markdown to HTML (synchronously)
            const parsedResult = marked.parse(props.message.content || '', { async: false });

            // Type guard to ensure we have a string
            const rawHTML: string = typeof parsedResult === 'string' ? parsedResult : String(parsedResult || '');

            // Sanitize the HTML
            const sanitized = DOMPurify.sanitize(rawHTML, {
                ALLOWED_TAGS: [
                    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'a', 'code', 'pre', 'blockquote',
                    'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img', 'hr',
                    'strong', 'em', 'br',
                ],
                ALLOWED_ATTR: [
                    'href', 'src', 'alt', 'title', 'class', 'target', 'rel',
                ],
                ALLOW_DATA_ATTR: false,
            });

            // Add target="_blank" and rel="noopener noreferrer" to all links
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = sanitized;
            const links = tempDiv.querySelectorAll('a');
            links.forEach(link => {
                link.setAttribute('target', '_blank');
                link.setAttribute('rel', 'noopener noreferrer');
            });

            return tempDiv.innerHTML;
        } catch {
            // Fallback to empty string on any error
            return '';
        }
    });

    const messageSender: ComputedRef<string> = computed(() => props.message.role === 'assistant' ? 'bot' : props.message.role);

    const textColor: ComputedRef<string> = computed(() => getContrastTextColor(props.primaryColor ?? '#00453a'));

    // 4. Methods / Functions
    const formatTime = (date: Date): string => new Date(date)
        .toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
        });
</script>

<template>
    <div
        class="message-wrapper"
        :class="messageSender"
        :style="messageSender === 'user' ? `--text-primary: ${textColor};` : undefined"
    >
        <div
            class="message-bubble"
            :class="messageSender"
        >
            <div
                class="message-text markdown-content"
                v-html="renderedMarkdown"
            />
            <div class="message-time">
                {{ formatTime(message.createdAt) }}
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.message-wrapper {
    display: flex;
    animation: slide-in 0.3s ease-out;
    color: unset;
    margin-block-end: var(--spacing-03);
}

.message-wrapper.user {
    justify-content: flex-end;
}

.message-wrapper.bot {
    justify-content: flex-start;
}

.message-bubble {
    border-radius: var(--border-radius-md);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.1);
    max-inline-size: var(--chat-message-max-width);
    padding-block: var(--spacing-3x);
    padding-inline: var(--spacing-03);
}

.message-bubble.user {
    background: var(--chat-primary-color);
    border-end-end-radius: var(--border-radius-sm);
    color: var(--text-primary-invert);
}

.message-bubble.bot {
    background: var(--background-secondary);
    border-end-start-radius: var(--border-radius-sm);
    color: var(--text-primary);
}

.message-text {
    font-size: var(--font-regular);
    line-height: var(--line-height-150);
    word-wrap: break-word;
}

/* Markdown content styling */
.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3),
.markdown-content :deep(h4),
.markdown-content :deep(h5),
.markdown-content :deep(h6) {
    font-weight: var(--font-weight-semi-bold);
    line-height: var(--line-height-120);
    margin-block: var(--spacing-03) var(--spacing-02);
}

.markdown-content :deep(h1) {
    border-block-end: 1px solid rgb(0 0 0 / 0.1);
    font-size: var(--font-xxlarge);
    padding-block-end: var(--spacing-02);
}

.markdown-content :deep(h2) {
    border-block-end: 1px solid rgb(0 0 0 / 0.1);
    font-size: var(--font-xlarge);
    padding-block-end: var(--spacing-02);
}

.markdown-content :deep(h3) {
    font-size: var(--font-large);
}

.markdown-content :deep(h4) {
    font-size: var(--font-medium);
}

.markdown-content :deep(h5),
.markdown-content :deep(h6) {
    font-size: var(--font-regular);
}

.markdown-content :deep(p) {
    font-size: var(--font-regular);
    line-height: var(--line-height-150);
    margin-block: 0 var(--spacing-3x);
}

.markdown-content :deep(p:last-child) {
    margin-block-end: 0;
}

.markdown-content :deep(a) {
    color: inherit;
    opacity: 0.9;
    text-decoration: underline;
    word-break: break-word;
}

.markdown-content :deep(a:hover) {
    opacity: 1;
}

.message-bubble.user .markdown-content :deep(a) {
    color: var(--text-primary-invert);
}

.message-bubble.bot .markdown-content :deep(a) {
    color: var(--text-info);
}

.markdown-content :deep(code) {
    border-radius: var(--border-radius-xs);
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', 'Fira Mono', 'Roboto Mono', 'Courier New', monospace;
    font-size: var(--font-small);
    padding-block: calc(var(--spacing-base) / 2);
    padding-inline: calc(var(--spacing-base) * 1.25);
}

.message-bubble.user .markdown-content :deep(code) {
    background-color: rgb(255 255 255 / 0.2);
}

.message-bubble.bot .markdown-content :deep(code) {
    background-color: rgb(0 0 0 / 0.08);
}

.markdown-content :deep(pre) {
    padding: var(--spacing-3x);
    border-radius: var(--border-radius-xs);
    line-height: var(--line-height-150);
    margin-block: var(--spacing-3x);
    margin-inline: 0;
    overflow-x: auto;
}

.message-bubble.user .markdown-content :deep(pre) {
    background-color: rgb(0 0 0 / 0.2);
}

.message-bubble.bot .markdown-content :deep(pre) {
    background-color: var(--background-overlay-invert);
}

.markdown-content :deep(pre code) {
    padding: 0;
    border-radius: 0;
    background-color: transparent;
}

.markdown-content :deep(blockquote) {
    border-inline-start: var(--spacing-base) solid rgb(0 0 0 / 0.2);
    font-style: italic;
    margin-block: var(--spacing-3x);
    margin-inline: 0;
    opacity: 0.85;
    padding-block: var(--spacing-01);
    padding-inline-start: var(--spacing-3x);
}

.markdown-content :deep(blockquote p) {
    margin-block: 0;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
    display: block;
    line-height: var(--line-height-150);
    margin-block: var(--spacing-3x);
    margin-inline: 0;
    padding-inline-start: var(--spacing-6x);
}

.markdown-content :deep(ul li) {
    list-style-type: disc;
}

.markdown-content :deep(ol li) {
    list-style-type: decimal;
}

.markdown-content :deep(li) {
    display: list-item;
    margin-block: calc(var(--spacing-base) * 1.5);
    margin-inline: 0;
    padding-inline-start: var(--spacing-01);
}

.markdown-content :deep(li::marker) {
    color: currentcolor;
    font-weight: var(--font-weight-semi-bold);
}

.markdown-content :deep(li > p) {
    margin-block: var(--spacing-01);
}

.markdown-content :deep(ul ul),
.markdown-content :deep(ol ul),
.markdown-content :deep(ul ol),
.markdown-content :deep(ol ol) {
    margin-block: var(--spacing-01);
}

.markdown-content :deep(ul ul) {
    list-style-type: circle;
}

.markdown-content :deep(ul ul ul) {
    list-style-type: square;
}

.markdown-content :deep(table) {
    border-collapse: collapse;
    font-size: var(--font-small);
    inline-size: 100%;
    margin-block: var(--spacing-3x);
    margin-inline: 0;
}

.markdown-content :deep(table th),
.markdown-content :deep(table td) {
    border: 1px solid rgb(0 0 0 / 0.2);
    line-height: var(--line-height-120);
    padding-block: var(--spacing-2x);
    padding-inline: calc(var(--spacing-base) * 2.5);
    text-align: start;
}

.markdown-content :deep(table th) {
    font-weight: var(--font-weight-semi-bold);
}

.message-bubble.user .markdown-content :deep(table th) {
    background-color: rgb(255 255 255 / 0.1);
}

.message-bubble.bot .markdown-content :deep(table th) {
    background-color: var(--background-overlay-invert);
}

.markdown-content :deep(img) {
    border-radius: var(--border-radius-sm);
    block-size: auto;
    margin-block: var(--spacing-3x);
    margin-inline: 0;
    max-inline-size: 100%;
}

.markdown-content :deep(hr) {
    border: none;
    border-block-start: 1px solid rgb(0 0 0 / 0.2);
    margin-block: var(--spacing-03);
    margin-inline: 0;
}

.markdown-content :deep(strong) {
    font-weight: var(--font-weight-semi-bold);
}

.markdown-content :deep(em) {
    font-style: italic;
}

.message-time {
    font-size: var(--font-small);
    line-height: var(--line-height-120);
    margin-block-start: calc(var(--spacing-base) * 1.5);
    opacity: 0.7;
}

@keyframes slide-in {
    from {
        opacity: 0;
        transform: translateY(var(--spacing-02));
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
